import { useEffect, useRef, useState } from 'react';
import { GAME_VERSION } from '@/domain/constants';
import './IntroLoader.css';

/**
 * Cinematic boot sequence: full-screen muted intro video as background while
 * a series of "loading" steps tick through. Steps are mostly cosmetic flavour
 * (we're already on a 60-Hz game tick), but they buy a few seconds for the
 * actual hydration promise to resolve in the background — and they sell the
 * "you're booting into a real cyberpunk OS" vibe.
 *
 * The component:
 *   1. Plays the intro video looped + muted (autoplay-friendly).
 *   2. Walks through a list of step descriptions with realistic-feeling delays.
 *   3. Once all steps are done AND the optional `ready` flag is true, fades
 *      out and calls onComplete() so App can render the actual UI.
 */

const VIDEO_URL = '/Intro-Video.mp4';

interface BootStep {
  label: string;
  /** ms — purely cosmetic so the bar feels like real work happening */
  duration: number;
}

const STEPS: BootStep[] = [
  { label: 'Verbinde mit Mainframe', duration: 600 },
  { label: `Versions-Check · v${GAME_VERSION}`, duration: 480 },
  { label: 'Authentifizierung verifiziert', duration: 420 },
  { label: 'Lade Runner-Profil', duration: 720 },
  { label: 'Entschlüssele Equipment-Stash', duration: 580 },
  { label: 'Synchronisiere Decrypter-Netzwerk', duration: 640 },
  { label: 'Kalibriere Skill-Web', duration: 520 },
  { label: 'Initialisiere Combat-Engine', duration: 580 },
  { label: 'Lade 3D-Assets', duration: 620 },
  { label: 'System bereit', duration: 900 },
];

/** Extra hold after all steps finish, so the player sees the "INITIALISIERT" state. */
const POST_DONE_HOLD_MS = 700;

interface Props {
  /** Optional gate — if false, hold at "System bereit" until it flips true. */
  ready?: boolean;
  /** Fired once the sequence finishes AND `ready` is true. */
  onComplete: () => void;
}

export function IntroLoader({ ready = true, onComplete }: Props) {
  const [stepIdx, setStepIdx] = useState(0);
  const [progress, setProgress] = useState(0);
  const [done, setDone] = useState(false);
  const [exiting, setExiting] = useState(false);
  const completeFiredRef = useRef(false);

  // Walk through steps with their delays, advancing progress smoothly between them.
  useEffect(() => {
    let cancelled = false;
    let cumulative = 0;
    const total = STEPS.reduce((s, x) => s + x.duration, 0);
    const tick = (i: number) => {
      if (cancelled) return;
      if (i >= STEPS.length) {
        setDone(true);
        setProgress(100);
        return;
      }
      setStepIdx(i);
      const stepStart = cumulative;
      const stepEnd = cumulative + STEPS[i].duration;
      // Animate progress from stepStart→stepEnd over the duration
      const startTime = performance.now();
      const animate = () => {
        if (cancelled) return;
        const elapsed = performance.now() - startTime;
        const t = Math.min(1, elapsed / STEPS[i].duration);
        const cur = stepStart + (stepEnd - stepStart) * t;
        setProgress((cur / total) * 100);
        if (t < 1) {
          requestAnimationFrame(animate);
        } else {
          cumulative = stepEnd;
          tick(i + 1);
        }
      };
      requestAnimationFrame(animate);
    };
    tick(0);
    return () => { cancelled = true; };
  }, []);

  // Fire onComplete once both: cosmetic sequence done + ready flag true.
  // We deliberately hold on the "System bereit / INITIALISIERT" state for
  // POST_DONE_HOLD_MS so the player actually registers the completion before
  // the screen fades to black.
  useEffect(() => {
    if (!done || !ready || completeFiredRef.current) return;
    completeFiredRef.current = true;
    const holdId = window.setTimeout(() => {
      setExiting(true);
      // Allow CSS fade-out (350 ms) before unmounting
      window.setTimeout(() => onComplete(), 380);
    }, POST_DONE_HOLD_MS);
    return () => window.clearTimeout(holdId);
  }, [done, ready, onComplete]);

  return (
    <div className={`intro-loader ${exiting ? 'intro-loader--exiting' : ''}`}>
      {/* Background video — muted + loop + autoplay-friendly */}
      <video
        className="intro-loader__video"
        src={VIDEO_URL}
        autoPlay
        muted
        loop
        playsInline
        // Preload metadata only — don't block on full download.
        preload="auto"
      />

      {/* Gradient overlay for legibility */}
      <div className="intro-loader__overlay" aria-hidden />

      {/* Centered HUD */}
      <div className="intro-loader__hud">
        <div className="intro-loader__brand">
          <div className="intro-loader__title">CYBER-VAULT</div>
          <div className="intro-loader__subtitle">EXTRACTION PROTOCOL</div>
        </div>

        <div className="intro-loader__bar">
          <div className="intro-loader__bar-track">
            <div
              className="intro-loader__bar-fill"
              style={{ width: `${progress}%` }}
            />
          </div>
          <div className="intro-loader__bar-info">
            <span className="intro-loader__step">
              {done && ready ? '▶ INITIALISIERT' :
                done && !ready ? '⌛ Warte auf Server …' :
                `▸ ${STEPS[stepIdx]?.label ?? '…'}`}
            </span>
            <span className="intro-loader__pct">{Math.round(progress)} %</span>
          </div>
        </div>

        <div className="intro-loader__steps">
          {STEPS.map((s, i) => (
            <span
              key={i}
              className={`intro-loader__step-tick ${
                i < stepIdx || done ? 'intro-loader__step-tick--done' : ''
              } ${i === stepIdx && !done ? 'intro-loader__step-tick--active' : ''}`}
            >
              {i < stepIdx || done ? '✓' : i === stepIdx && !done ? '◌' : '·'} {s.label}
            </span>
          ))}
        </div>
      </div>

      {/* Corner watermark */}
      <div className="intro-loader__version">BUILD v{GAME_VERSION}</div>
    </div>
  );
}
